import type { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Skeleton } from '~/components';
import { cn } from '~/lib/cn';
import { env } from '~/lib/env';
import {
  TableBody,
  TableCell,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { SkeletonTable } from '~/modules/artist-roster/components/Table/SkeletonTable';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import type { ArtistDetails, AssetFamily } from '../../lib/types';
import { DataError } from '../DataError/DataError';
import type { TrackTableMeta } from './TrackTable';

interface TrackTableBodyProps {
  table: Table<AssetFamily>;
  meta: TrackTableMeta;
  artistData: ArtistDetails;
  isFetchingTracks: boolean;
  isLoadingPerformance: boolean;
}

const PERFORMANCE_COLUMNS = [
  'streamsRTD',
  'streams',
  'organicStreams',
  'socialCreations',
];

export const TrackTableBody = ({
  table,
  meta,
  artistData,
  isFetchingTracks,
  isLoadingPerformance,
}: TrackTableBodyProps) => {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');

  const isTrackDetailsEnabled =
    env('VITE_WMG_TRACK_DETAILS_ENABLED') === 'true';
  const isAssetPerformanceEnabled =
    env('VITE_WMG_ASSET_PERFORMANCE_ENABLED') === 'true';

  if (isFetchingTracks) {
    return (
      <TableBody>
        <SkeletonTable
          size={12}
          headers={table.getHeaderGroups()[0].headers}
          firstLineClassName="w-3xl"
          secondLineClassName="w-2xl"
          cellClassName="pl-sm"
        />
      </TableBody>
    );
  }

  if (!table.getRowModel().rows?.length) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={isAssetPerformanceEnabled ? 6 : 4}>
            <DataError
              title={t('trackList.noData.title')}
              subtitle={t('trackList.noData.sub', {
                artistName: meta?.artistName || 'artist',
              })}
              icon={
                <Icon
                  type="turntable"
                  size="4x"
                  variant="solid"
                  aria-hidden
                  className="text-default"
                />
              }
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const handleTrackTableRowClick = ({
    trackData,
  }: {
    trackData: AssetFamily;
  }) => {
    if (isTrackDetailsEnabled && meta) {
      if (artistData) {
        trackEvent('artistTrackListTrackClick', {
          artist_id: artistData.partyId,
          artist_name: artistData.name,
          track_name: trackData.title,
          // @TODO WMG1-246 Add Family ID when available.
          family_id: trackData.id,
        });
      }
    }
  };

  return (
    <TableBody>
      {table.getRowModel().rows.map((row, index) => (
        <TableRow
          aria-label={`Row for ${row.original?.id || 'track'}`}
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          className={
            isTrackDetailsEnabled
              ? cn(
                  'group/row relative transition-all duration-75 hover:bg-emphasis-subtle',
                  'hover:!opacity-full group-hover/table:opacity-stronger',
                )
              : ''
          }
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell
              key={cell.id}
              scope="row"
              className={cn(
                'border-y p-0 transition-all duration-75 border-transparent last:py-xs',
                'group-hover/row:border-emphasis-strong',
                'first:rounded-l-sm first:border-l',
                'last:rounded-r-sm last:border-r last:pr-sm',
                index % 2 === 0 && 'bg-default-inverse/default',
                !isAssetPerformanceEnabled && 'first:pl-lg',
              )}
            >
              {PERFORMANCE_COLUMNS.includes(cell.column.id) &&
              isLoadingPerformance ? (
                <div className="space-y-sm pl-sm">
                  <Skeleton className="h-md w-3xl rounded-sm bg-level-04" />
                  <Skeleton className="h-md w-2xl rounded-sm bg-level-04" />
                </div>
              ) : cell.column.id === 'title' && isTrackDetailsEnabled ? (
                <Link
                  // @TODO WMG1-246 Replace track title with track family ID
                  to={`/roster/${meta.partyId}/${row.original.id}`}
                  className="flex items-center gap-sm py-sm pl-lg hover:underline"
                  onClick={() =>
                    handleTrackTableRowClick({ trackData: row.original })
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Link>
              ) : (
                <div
                  className={cn(
                    'flex items-center gap-sm py-sm',
                    cell.column.id === 'title' ? 'pl-lg' : '',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};

TrackTableBody.displayName = 'TrackTableBody';
